import * as React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { FacilityInspectionChangeRecord, InspectionPointStatus, SafetyInspection, SafetyInspectionFacility, SafetyInspectionLocation, SafetyInspectionPoint, fieldValidation } from '../../../../../amorg-common';
import { getFacilityInspectionHistory, getSafetyInspection, updateFacilityInspection } from '../../../../../api/safetyInspectionClientApi';
import { Button } from '../../../../../designSystem/button/Button';
import { Card } from '../../../../../designSystem/Card';
import { Flex } from '../../../../../designSystem/compositeComponents/FlexHelpers';
import { SingleColumnLayout } from '../../../../_layouts/SingleColumnLayout';
import { LoadingPageOverlay } from '../../../../../designSystem/LoadingPageOverlay';
import { MessageCard } from '../../../../../designSystem/Message';
import { PageHeader } from '../../../../../designSystem/PageHeader';
import { P } from '../../../../../designSystem/typography/Body';
import { clientAccessHelper } from '../../../../../utils/clientAccessHelper';
import { ProtectedPageContext } from '../../../../_contexts/ProtectedPageContext';
import { UserPageWrapper } from '../../../../_pageWrappers/UserPageWrapper';
import { InspectionHistoryPanel } from '../../_components/inspectionPoint/InspectionHistoryPanel';
import { InspectionImagesPanel } from '../../_components/inspectionPoint/InspectionImagesPanel';
import { InspectionOverview } from '../../_components/inspectionPoint/InspectionOverview';
import { Tabber2 } from '../../../../../designSystem/Tabber2';

type InspectionState = {
    status:InspectionPointStatus,
    comment:string
}
export type StateDetails = {
    locationIndex:number,
    location:SafetyInspectionLocation,
    facilityIndex:number,
    facility:SafetyInspectionFacility,
    inspectionIndex:number,
    inspection:SafetyInspectionPoint,
    currentTotalIndex:number,
    allInspections:SafetyInspectionPoint[],
}

export default function InspectionPointPage(): JSX.Element 
{
    const { activityId } = useParams<{ activityId:string }>();

    return (
        <UserPageWrapper
            selectedMenuItemKey="safetyinspection"
            breadCrumbs={[
                {text:'Sikkerhedsrunderinger', url:'/safetyinspections'},
                {text:'Sikkerhedsrundering', url:`/safetyinspections/${activityId}`},
                {text:'Kontrolpunkt', url:''}
            ]}>
            <InspectionPointPageContent />
        </UserPageWrapper>
    );
}

function InspectionPointPageContent(): JSX.Element
{
    const params = useParams<{ activityId:string, facilityInspectionId:string }>();
    const activeUser = React.useContext(ProtectedPageContext).user;
    const hasAccess = clientAccessHelper.hasAllPermissions(activeUser, ['ViewAssignedSafetyInspections']);

    const [loading, setLoading] = React.useState(true);
    const [safetyInspection, setSafetyInspection] = React.useState<SafetyInspection | null>(null); 
    const [inEditState, setInEditState] = React.useState<InspectionState | null>(null); 
    const [redirect, setRedirect] = React.useState('');
    const [tab, setTab] = React.useState<'images'|'history'>('images');
    const [showDone, setShowDone] = React.useState(false);

    const [history, setHistory] = React.useState<FacilityInspectionChangeRecord[] | null    >(null);

    React.useEffect(function () {
        reloadFullState();
    }, [params.facilityInspectionId]);

    async function reloadFullState() {
        setLoading(true);

        if(!params.activityId || !params.facilityInspectionId)
            return;

        const si = await getSafetyInspection(params.activityId);
        if(!si){
            setLoading(false);
            return;
        }

        const filteredSafetyInspection:SafetyInspection = {
            activityId:si.activityId,
            locations:[],
        };
        for(const l of si.locations){
            if(l.facilities.some(f => f.inspectionPoints.length > 0)){
                filteredSafetyInspection.locations.push(l);

                for(const f of l.facilities){
                    if(f.inspectionPoints.length > 0)
                        l.facilities = l.facilities.filter(f => f.inspectionPoints.length > 0);
                }
            }
        }
        setSafetyInspection(filteredSafetyInspection);

        const details = getStateDetails(filteredSafetyInspection);

        const records = await getFacilityInspectionHistory(params.facilityInspectionId);
        setHistory(records);
        
        setInEditState({
            status:details.inspection.inspectionResult.status,
            comment:details.inspection.inspectionResult.comment,
        });

        setLoading(false);
    }

    function getStateDetails(safetyInspection:SafetyInspection):StateDetails
    {
        let currentTotalIndex = 0;
        let locationIndex = 0;
        const allInspections:SafetyInspectionPoint[] = [];
        let details:StateDetails | null = null;

        const locationsWithInspections = safetyInspection.locations.filter(l => l.facilities.some(f => f.inspectionPoints.length > 0)) || [];
        for(const l of locationsWithInspections)
        {
            let facilityIndex = 0;
            const facilitiesWithInspections = l.facilities.filter(f => f.inspectionPoints.length > 0);
            for(const f of facilitiesWithInspections)
            {
                let inspectionIndex = 0;
                for(const fi of f.inspectionPoints)
                {
                    if(fi.inspectionResult?.id === params.facilityInspectionId)
                    {
                        details = {
                            locationIndex,
                            location:l,
                            facilityIndex,
                            facility:f,
                            inspectionIndex,
                            inspection: fi,
                            currentTotalIndex,
                            allInspections:[],
                        }
                    }
                    allInspections.push(fi);
                    inspectionIndex++;
                    currentTotalIndex++;
                }
                facilityIndex++;
            }
            locationIndex++;
        }
        if(!details)
            throw new Error('Could not find inspection point with id ' + params.facilityInspectionId);

        details.allInspections = allInspections;
        return details;
    }

    async function handleClickPrevious()
    {
        if(!details || !inEditState)
            return;
        
        setShowDone(false);
        if(details.currentTotalIndex > 0)
        {
            setRedirect(`/safetyinspections/${params.activityId}/inspections/${
                details.allInspections[details.currentTotalIndex-1].inspectionResult.id
            }`);
        }
    }

    async function handleClickNext()
    {
        if(!details || !inEditState)
            return;

        const redirect = async () => {
            if(details.currentTotalIndex <  details.allInspections.length-1)
            {
                setRedirect(`/safetyinspections/${params.activityId}/inspections/${
                    details.allInspections[details.currentTotalIndex+1].inspectionResult.id
                }`);
            }
            else
            {
                await reloadFullState();
                setShowDone(true);
            }
        };

        const hasChanges = inEditState.comment !== details.inspection.inspectionResult.comment 
            || inEditState.status !== details.inspection.inspectionResult.status;
            
        if(!hasChanges){
            await redirect();
        }
        else{
            const validComment = inEditState.comment ? fieldValidation.facilityInspection.comment(inEditState.comment) : true;
            if(validComment && params.activityId && params.facilityInspectionId)
            {
                setLoading(true);
                const result = await updateFacilityInspection(params.activityId, params.facilityInspectionId, inEditState.status, inEditState.comment);
                
                if(result.isSuccess){
                    await redirect();
                }
                setLoading(false);
            }
        }
    }

    if (!hasAccess)
        return <P>Hov! Du har ikke rettigheder til at tilgå denne side.</P>;

    const details = safetyInspection ? getStateDetails(safetyInspection) : null;

    return (
        <>
            {redirect && <Navigate to={redirect} />}
            <LoadingPageOverlay loading={loading} />
            <PageHeader narrow headerText="Kontrolpunkt" />
            <SingleColumnLayout narrow>
                <>
                    {safetyInspection && inEditState && params.activityId && details &&
                        <Flex flexDirection="column" gap={32}>

                            {showDone &&
                                <MessageCard variant='info'>
                                    Sikkerhedsrunderingen indeholder ikke flere kontrolpunkter.<br />
                                    <br />
                                    <a href={`/safetyinspections/${safetyInspection.activityId}`}>Klik her for at gå tilbage til oversigten</a> 
                                </MessageCard>
                            }

                            <InspectionOverview 
                                activityId={params.activityId} 
                                details={details} 
                                safetyInspection={safetyInspection}
                                status={inEditState.status}
                                comment={inEditState.comment}
                                showSelection={!showDone}
                                onInspectionStateChange={(status,comment) => setInEditState({status, comment})}
                                onRedirect={(url) => setRedirect(url)} />
                            
                            <Flex flexDirection='column' gap={1}>
                                <Tabber2
                                    variant='button'
                                    items={[
                                        {tabName:'images', text:`Billeder (${details.inspection.inspectionResult.files.length})`},
                                        {tabName:'history', text:`Historik (${history?.length || 0})`},
                                    ]}
                                    selectedTabName={tab}
                                    onClick={(tabName) => setTab(tabName as 'images'|'history')}
                                />
                                <Card variant="outlined" marginTop={0}>
                                    {tab === 'images' &&
                                        <InspectionImagesPanel inspection={details.inspection.inspectionResult} onUpdated={() => reloadFullState()} />
                                    }
                                    {tab === 'history' &&
                                        <InspectionHistoryPanel history={history || []} />
                                    }
                                </Card>
                            </Flex>

                            <Flex flexDirection='row' gap={10} justifyContent='center'>
                                <Button variant='outlined' text='Forrige' icon='arrows_chevron_chevronBack' iconPosition="left" disabled={details.currentTotalIndex <= 0} onClick={handleClickPrevious} />

                                <Button variant='outlined' text='Næste' icon='arrows_chevron_chevronForward' iconPosition="right" onClick={handleClickNext} />
                            </Flex>
                        </Flex>
                    }
                </>
            </SingleColumnLayout>
        </>
    );
}